import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, set, useForm } from "react-hook-form";

import { Button, Input, Select, SelectItem } from "@nextui-org/react";
import { ArrowRight } from "lucide-react";
import ContactSchema from "../../../schemas/ContactSchema";
import { axios } from "../../../lib/axios";
import { toast } from 'sonner';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameInput, setNameInput] = useState(null);
  const [phoneInput, setPhoneInput] = useState(null);
  const [emailInput, setEmailInput] = useState(null);
  const [travelBloggerTypesInput, setTravelBloggerTypesInput] = useState(null);
  const [tourismSpecializationsInput, setTourismSpecializationsInput] = useState(null);
  const [travelCompanionsInput, setTravelCompanionsInput] = useState(null);
  const [audienceNeedsInput, setAudienceNeedsInput] = useState(null);
  const [contentTypesInput, setContentTypesInput] = useState(null);
  const [socialMediaLinkInput, setSocialMediaLinkInput] = useState(null);
  const {
    register,
    control,
    handleSubmit,
    reset,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(ContactSchema),
  });

  useEffect(() => {
    const getFormInputsData = async () => {
      try {
        const response = await axios.get("/api/questions");
        const formInputs = response.data?.data;
        setNameInput(formInputs?.find(input => input?.question === "Name"));
        setPhoneInput(formInputs?.find(input => input?.question === "Phone Number"));
        setEmailInput(formInputs?.find(input => input?.question === "Email Address"));
        setTravelBloggerTypesInput(formInputs?.find(input => input?.question === "What type of travel blogger are you?"));
        setTourismSpecializationsInput(formInputs?.find(input => input?.question === "What type of tourism do you support and specialize in?"));
        setTravelCompanionsInput(formInputs?.find(input => input?.question === "Who are your travel companions?"));
        setAudienceNeedsInput(formInputs?.find(input => input?.question === "Which of the following best fits your audience's needs and blogging strategy?"));
        setContentTypesInput(formInputs?.find(input => input?.question === "What type of content do you provide?"));
        setSocialMediaLinkInput(formInputs?.find(input => input?.question === "Social media link for your most used platform"));
      } catch (error) {
        console.error("Error fetching form inputs:", error);
      }
    };

    getFormInputsData();
  }, []);

  const onSubmit = (data) => {
    setIsSubmitting(true);
    // console.log(data);

    axios
      .post("/api/contestant", data)
      .then((response) => {
        console.log("Contact form submitted successfully:", response.data);
        toast.success("Contact form submitted successfully!");
        reset({
          name: "",
          phone_number: "",
          email: "",
          travel_blogger_types: [],
          tourism_specializations: [],
          travel_companions: [],
          audience_needs: [],
          content_types: [],
          social_media_link: "",
        });

        clearErrors();
      })
      .catch((error) => {

        console.error("Error submitting contact form:", error);
        toast.error(error?.response?.data?.message || "Error submitting contact form. Please try again later.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  return (
    <div className="bg-[url('/public/images/contact/form.png')] bg-cover bg-center mt-8">
      <div className="container md:px-[40px] min-w-full my-6 sm:my-12">
        <div className="bg-white mx-auto max-w-[680px] rounded-[25px] formShadow px-4 sm:px-[110px] py-[60px] sm:py-[90px]">
          <h2 className="sm:text-[48px] text-[32px] text-textColor font-bold text-center leading-[48px]">
            Fill the form and start the journey
          </h2>
          <form
            className="flex flex-col gap-6 mt-[40px] sm:mt-[60px]"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex gap-2 sm:gap-7 min-[460px]:flex-nowrap flex-wrap">
              <Input
                isInvalid={errors.name}
                errorMessage={errors.name?.message}
                {...register("name")}
                variant="bordered"
                placeholder={nameInput?.placeholder}
                label="Name"
                labelPlacement="outside"
                classNames={{
                  base: "min-[460px]:w-auto w-full",
                  inputWrapper: "rounded-[6px] border-[1px] inputShadow",
                  label: "font-bold text-sm",
                }}
              />
              <Input
                isInvalid={errors.phone_number}
                errorMessage={errors.phone_number?.message}
                {...register("phone_number")}
                variant="bordered"
                placeholder={phoneInput?.placeholder}
                label="Phone Number"
                labelPlacement="outside"
                classNames={{
                  base: "min-[460px]:w-auto w-full",
                  inputWrapper: "rounded-[6px] border-[1px] inputShadow",
                  label: "font-bold text-sm",
                }}
              />
            </div>
            <Input
              isInvalid={errors.email}
              errorMessage={errors.email?.message}
              {...register("email")}
              variant="bordered"
              placeholder={emailInput?.placeholder}
              label="Email Address"
              labelPlacement="outside"
              classNames={{
                inputWrapper: "rounded-[6px] border-[1px] inputShadow",
                label: "font-bold text-sm",
              }}
            />
            <Controller
              name="travel_blogger_types"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  isInvalid={errors.travel_blogger_types}
                  errorMessage={errors.travel_blogger_types?.message}
                  selectedKeys={new Set(field.value)}
                  onSelectionChange={(keys) => {
                    const selectedArray = Array.from(keys ?? []);
                    field.onChange(selectedArray);
                  }}
                  selectionMode="multiple"
                  variant="bordered"
                  label="What type of travel blogger are you?"
                  placeholder={travelBloggerTypesInput?.placeholder || "Choose your answer"}
                  labelPlacement="outside"
                  classNames={{
                    trigger: "border-[1px] rounded-[6px] inputShadow",
                    label: "font-bold text-sm",
                  }}
                >
                  {travelBloggerTypesInput?.options?.map((option) => (
                    <SelectItem key={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="tourism_specializations"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  isInvalid={errors.tourism_specializations}
                  errorMessage={errors.tourism_specializations?.message}
                  selectedKeys={new Set(field.value)}
                  onSelectionChange={(keys) => {
                    const selectedArray = Array.from(keys ?? []);
                    field.onChange(selectedArray);
                  }}
                  selectionMode="multiple"
                  variant="bordered"
                  label="What type of tourism do you support and specialize in?"
                  placeholder={tourismSpecializationsInput?.placeholder || "Choose your answer"}
                  labelPlacement="outside"
                  classNames={{
                    trigger: "border-[1px] rounded-[6px] inputShadow mt-6",
                    label: "font-bold text-sm text-left",
                  }}
                >
                  {tourismSpecializationsInput?.options?.map((option) => (
                    <SelectItem key={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="travel_companions"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  isInvalid={errors.travel_companions}
                  errorMessage={errors.travel_companions?.message}
                  selectedKeys={new Set(field.value)}
                  onSelectionChange={(keys) => {
                    field.onChange(Array.from(keys)); // Convert Set back to array
                  }}
                  selectionMode="multiple"
                  variant="bordered"
                  label="Who are your travel companions?"
                  placeholder={travelCompanionsInput?.placeholder || "Choose your answer"}
                  labelPlacement="outside"
                  classNames={{
                    trigger: "border-[1px] rounded-[6px] inputShadow",
                    label: "font-bold text-sm",
                  }}
                >
                  {travelCompanionsInput?.options?.map((option) => (
                    <SelectItem key={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="audience_needs"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  isInvalid={errors.audience_needs}
                  errorMessage={errors.audience_needs?.message}
                  selectedKeys={new Set(field.value)}
                  onSelectionChange={(keys) => {
                    field.onChange(Array.from(keys)); // Convert Set back to array
                  }}
                  selectionMode="multiple"
                  variant="bordered"
                  label="Which of the following best fits your audience's needs and blogging strategy?"
                  placeholder={audienceNeedsInput?.placeholder || "Choose your answer"}
                  labelPlacement="outside"
                  classNames={{
                    trigger: "border-[1px] rounded-[6px] inputShadow mt-6",
                    label: "font-bold text-sm text-left",
                  }}
                >
                  {audienceNeedsInput?.options?.map((option) => (
                    <SelectItem key={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="content_types"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  isInvalid={errors.content_types}
                  errorMessage={errors.content_types?.message}
                  selectedKeys={new Set(field.value)}
                  onSelectionChange={(keys) => {
                    field.onChange(Array.from(keys)); // Convert Set back to array
                  }}
                  selectionMode="multiple"
                  variant="bordered"
                  label="What type of content do you provide?"
                  placeholder={contentTypesInput?.placeholder || "Choose your answer"}
                  labelPlacement="outside"
                  classNames={{
                    trigger: "border-[1px] rounded-[6px] inputShadow mt-6",
                    label: "font-bold text-sm text-left",
                  }}
                >
                  {contentTypesInput?.options?.map((option) => (
                    <SelectItem key={option}>
                      {option}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />

            <Input
              isInvalid={errors.social_media_link}
              errorMessage={errors.social_media_link?.message}
              {...register("social_media_link")}
              variant="bordered"
              placeholder={socialMediaLinkInput?.placeholder}
              label="Social media link for your most used platform"
              labelPlacement="outside"
              classNames={{
                inputWrapper: "rounded-[6px] border-[1px] inputShadow",
                label: "font-bold text-sm",
              }}
            />
            <Button
              type="submit"
              radius="sm"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              className="flex items-center gap-2 text-sm text-white bg-black"
            >
              Send message <ArrowRight size={15} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
